import { useCallback, useEffect, useState } from "react";
import type { AthleteTargets } from "@jackedlogs/shared";

// The coach-detail ↔ targets-editor swap, shared by both thin mounts (mobile
// CoachAthleteDetailScreen + web-rnw AthleteDetailScreen) so the identical
// "swap in the editor, refresh on save" state machine lives in ONE place. The
// platform seams — the ProgressDeps wiring, the Android BackHandler, goBack vs
// an onBack prop — stay in each host; only this bookkeeping is shared.
//
// ME-3: targets moved off the program onto the athlete's own AthleteTargets doc
// (the meter reads it now), so the swap holds the ATHLETE'S targets, not a
// program. `targets` is seeded from the host's fetched doc and then owned
// locally, so a saved edit (`onSaved`) refreshes the scoring panel WITHOUT a
// re-fetch (a save must refresh dependent pages). `editing` is a plain open
// flag — the editor edits the athlete partition, so there is no program id to
// resolve.
export function useTargetsEditorSwap(
  loadedTargets: AthleteTargets | null,
): {
  /** The athlete's targets, refreshed in place on save (feeds the panel). */
  targets: AthleteTargets | null;
  /** Whether the editor is swapped in over the detail. */
  editing: boolean;
  /** Open the editor (the panel's onEditTargets). */
  openEditor: () => void;
  closeEditor: () => void;
  /** Merge the saved targets back so the scoring panel re-renders fresh. */
  onSaved: (updated: AthleteTargets) => void;
} {
  const [targets, setTargets] = useState<AthleteTargets | null>(loadedTargets);
  useEffect(() => setTargets(loadedTargets), [loadedTargets]);

  const [editing, setEditing] = useState(false);
  const openEditor = useCallback(() => setEditing(true), []);
  const closeEditor = useCallback(() => setEditing(false), []);
  const onSaved = useCallback((updated: AthleteTargets) => setTargets(updated), []);

  return { targets, editing, openEditor, closeEditor, onSaved };
}
